import styled, { keyframes } from "styled-components";
import React from "react";
import { Loader2, LogOut } from "lucide-react";
import { router } from "@inertiajs/react";

import { useRoom } from "../contexts/room";
import { useLanguage } from "../contexts/language";

const delJson = (url) =>
  fetch(url, {
    method: "DELETE",
    headers: { Accept: "application/json" },
    credentials: "same-origin",
  }).then((res) => res.json().then((json) => ({ ok: res.ok, json })));

// A confirm-gated "exit the guest session and start over" control for the post-login HGS screens
// (My Bookings, Booking Detail). Ends the booking-scoped session via DELETE /session, clears the
// cached profile, then returns to the start of the self-deposit flow so the guest can look up / input
// a different booking. Best-effort: even if the DELETE fails (the cookie may already be invalid) we
// still clear local state and navigate — the self-deposit page re-checks the session server-side and
// shows the lookup step when none remains.
export const ExitSessionButton = (props) => {
  const { t } = useLanguage();
  const { clearRoom } = useRoom();
  const copy = t.exitSession;
  const buildingCode = props.buildingCode;

  const [open, setOpen] = React.useState(false);
  const [exiting, setExiting] = React.useState(false);

  const exit = () => {
    setExiting(true);
    delJson("/api/v1/guest_portal/session")
      .catch(() => {})
      .finally(() => {
        clearRoom(buildingCode);
        router.visit(`/hebe/${buildingCode}/hgs/self-deposit`);
      });
  };

  return (
    <>
      <ExitRow>
        <ExitButton type="button" onClick={() => setOpen(true)}>
          <LogOut size={16} /> {copy.button}
        </ExitButton>
      </ExitRow>

      {open ? (
        <ModalOverlay onClick={() => (exiting ? null : setOpen(false))}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalTitle>{copy.confirmTitle}</ModalTitle>
            <ModalBody>{copy.confirmBody}</ModalBody>
            <ModalActions>
              <Secondary
                type="button"
                disabled={exiting}
                onClick={() => setOpen(false)}
              >
                {copy.confirmCancel}
              </Secondary>
              <Danger type="button" disabled={exiting} onClick={exit}>
                {exiting ? (
                  <Loader2 size={16} className="spin" />
                ) : (
                  copy.confirmYes
                )}
              </Danger>
            </ModalActions>
          </ModalContent>
        </ModalOverlay>
      ) : null}
    </>
  );
};

const ExitRow = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 24px;
`;

const ExitButton = styled.button`
  align-items: center;
  background: none;
  border: none;
  color: #9aa0a6;
  cursor: pointer;
  display: inline-flex;
  font-size: 14px;
  gap: 6px;
  padding: 6px 10px;
  text-decoration: underline;
  transition: color 0.2s;

  &:hover {
    color: #c0392b;
  }
`;

const fadeInScale = keyframes`
  from {
    opacity: 0;
    transform: scale(0.96) translateY(8px);
  }
  to {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
`;

const ModalOverlay = styled.div`
  align-items: center;
  backdrop-filter: blur(4px);
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  inset: 0;
  justify-content: center;
  padding: 16px;
  position: fixed;
  z-index: 50;
`;

const ModalContent = styled.div`
  animation: ${fadeInScale} 0.25s ease-out;
  background: #fff;
  border-radius: 16px;
  border-top: 3px solid #c9a961;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
  max-width: 420px;
  padding: 22px;
  width: 100%;
`;

const ModalTitle = styled.h2`
  color: #1a1a1a;
  font-size: 17px;
  font-weight: 700;
  margin: 0;
`;

const ModalBody = styled.p`
  color: #4b5563;
  font-size: 14px;
  line-height: 1.55;
  margin: 10px 0 0;
`;

const ModalActions = styled.div`
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  margin-top: 20px;
`;

const Secondary = styled.button`
  background: #f3f4f6;
  border: none;
  border-radius: 9px;
  color: #374151;
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  padding: 10px 16px;

  &:disabled {
    opacity: 0.6;
  }
`;

const Danger = styled.button`
  align-items: center;
  background: #c0392b;
  border: none;
  border-radius: 9px;
  color: #fff;
  cursor: pointer;
  display: inline-flex;
  font-size: 14px;
  font-weight: 600;
  gap: 6px;
  justify-content: center;
  min-width: 96px;
  padding: 10px 16px;

  &:disabled {
    opacity: 0.6;
  }

  .spin {
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;
